import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";

import { Spinner, Chip } from "@nextui-org/react";
import Select from "@/components/select/Select";
import Input from "@/components/input/Input";
import Button from "@/components/Button";

import LecturesSelection from "./LecturesSelection";
import CourseSelection from "./CourseSelection";

import { useOffers } from "../hooks/useOffers";
import useUpdateOffer from "../hooks/useUpdateOffer";
import { useSchoolLevelOptions } from "../../../../hooks/useSchoolLevelOptions";

function UpdateForm() {
    const { id } = useParams();
    const { state } = useLocation();

    const navigate = useNavigate();

    const { isUpdating, updateOffer } = useUpdateOffer();

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        getValues,
        setValue,
    } = useForm();

    const { isPending, offers } = useOffers({
        filter: { field: "id", value: id },
        includeFields: "lectures",
    });

    const offer = offers?.data[0];

    const lectuers =
        state?.lectures?.map((lecture) => lecture.id) ||
        offer?.relationships?.lectures?.map((lecture) => lecture.id);

    const [lecturesIds, setLecturesIds] = useState(lectuers);
    const [courseId, setCourseId] = useState(null);

    useEffect(() => {
        if (offer) {
            setValue("price", offer.attributes.price);
            setValue("school_level", offer.attributes.school_level);
            setValue("grade", offer.attributes.grade);
            setValue("name", offer.attributes.name);
            setValue("brief", offer.attributes.brief);
        }
    }, [offer, setValue]);

    const formValue = getValues();
    const selectedSchoolLevel = getValues("school_level");
    const { isSchoolLevelOptionsLoading, schoolLevelOptions, gradesOptions } = useSchoolLevelOptions(selectedSchoolLevel);

    function onSubmit(data) {
        if (lecturesIds.length)
            updateOffer(
                {
                    ...data,
                    lecture_ids: lecturesIds.filter(Boolean),
                    offerId: id,
                },
                {
                    onSuccess: () => {
                        navigate(`/admin/offers`);
                    },
                }
            );
    }

    const handleCloseChip = (id) => {
        const updatedIds = lecturesIds.filter((lectureId) => lectureId !== id);
        const filteredIds = updatedIds.filter(Boolean);
        setLecturesIds(filteredIds);
    };

    if (isPending) {
        return (
            <div className="w-full h-full py-5 mx-auto">
                <Spinner color="primary" />
            </div>
        );
    }

    return (
        <>
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-5 py-10"
            >
                <Select
                    name="school_level"
                    label="المستوى الدراسي*"
                    error={errors?.school_level?.message}
                    isOptionsLoading={isSchoolLevelOptionsLoading}
                    options={schoolLevelOptions}
                    size="fullWidth"
                    watch={watch}
                    register={register("school_level", {
                        required: "يجب اختيار السنة الدراسية",
                    })}
                />
                <Select
                    label="السنة الدراسية"
                    error={errors?.grade?.message}
                    options={gradesOptions}
                    name="grade"
                    size="fullWidth"
                    watch={watch}
                    register={register("grade", {
                        required: "يجب اختيار السنة الدراسية",
                    })}
                />
                <Input
                    label="أسم العرض"
                    name="name"
                    size="fullWidth"
                    error={errors?.name?.message}
                    register={register("name", {
                        required: "يجب ادخال اسم العرض",
                    })}
                />
                <Input
                    label="الوصف"
                    name="brief"
                    size="fullWidth"
                    error={errors?.brief?.message}
                    register={register("brief", {
                        required: "يجب ادخال وصف للعرض",
                    })}
                />
                <Input
                    label="السعر"
                    name="price"
                    type="number"
                    error={errors?.price?.message}
                    size="fullWidth"
                    register={register("price", {
                        required: "يجب ادخال السعر",
                        min: {
                            value: 0,
                            message: "السعر يجب ان لا يقل عن صفر",
                        },
                    })}
                />

                <div className="flex flex-col gap-3 py-10">
                    <p className="text-lg font-semibold text-primary-color-primary">
                        المحاضرات التي تم اختيارها داخل العرض قبل االتعديل:{" "}
                    </p>
                    <div>
                        {offer?.relationships?.lectures.map((lec) => {
                            return (
                                <Chip
                                    key={lec.id}
                                    variant="bordered"
                                    className="mx-2 text-lg font-semibold text-blue-color-primary"
                                >
                                    {lec?.attributes?.name}
                                </Chip>
                            );
                        })}
                    </div>
                </div>

                {formValue.grade && (
                    <CourseSelection
                        grade={formValue.grade}
                        setCourseId={setCourseId}
                    />
                )}

                {courseId && (
                    <LecturesSelection
                        courseId={courseId}
                        setLecturesIds={setLecturesIds}
                        lecturesIds={lecturesIds}
                    />
                )}

                {lecturesIds?.length ? (
                    <div className="flex flex-col gap-3 py-10">
                        <p className="text-lg font-semibold text-primary-color-primary">
                            المحاضرات التي تم اختيارها داخل العرض:
                        </p>
                        <div>
                            {lecturesIds.map(
                                (id) =>
                                    id !== null && (
                                        <Chip
                                            key={id}
                                            onClose={() => handleCloseChip(id)}
                                            variant="bordered"
                                            className="mx-2 text-lg font-semibold text-blue-color-primary"
                                        >
                                            {id}
                                        </Chip>
                                    )
                            )}
                        </div>
                    </div>
                ) : null}

                {lecturesIds.length ? (
                    <Button type="primary" disabled={isUpdating}>
                        تعديل
                    </Button>
                ) : null}
            </form>
        </>
    );
}

export default UpdateForm;
